import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR, formatCompactINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

export default function LoanEmiCalculator() {
  const [principal, setPrincipal] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, totalPayment, totalInterest, multiple, rows } = useMemo(() => {
    const p = Number(principal) || 0;
    const r = (Number(rate) || 0) / 12 / 100;
    const yrs = Number(years) || 0;
    const n = yrs * 12;
    if (!p || !n) return { emi: 0, totalPayment: 0, totalInterest: 0, multiple: 0, rows: [] };
    const emi = r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    const totalInterest = totalPayment - p;
    const multiple = p > 0 ? totalPayment / p : 0;

    // Year-by-year amortization
    const rows = [];
    for (let k = 1; k <= yrs; k++) {
      const m = k * 12;
      const balance =
        r === 0
          ? Math.max(0, p - emi * m)
          : Math.max(0, p * Math.pow(1 + r, m) - emi * ((Math.pow(1 + r, m) - 1) / r));
      const paidToDate = emi * m;
      const principalPaid = p - balance;
      const interestPaid = paidToDate - principalPaid;
      rows.push({
        year: k,
        primary: balance,
        columns: [
          { value: formatINR(interestPaid), accent: true },
          { value: formatINR(principalPaid) },
          { value: formatINR(balance) },
        ],
      });
    }
    return { emi, totalPayment, totalInterest, multiple, rows };
  }, [principal, rate, years]);

  const principalPct = totalPayment > 0 ? (Number(principal) / totalPayment) * 100 : 0;
  const interestPct = 100 - principalPct;

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Loan Amount (₹)" value={principal} onChange={setPrincipal} min={0} testId="emi-principal" />
          <NumField label="Interest Rate (% per annum)" value={rate} onChange={setRate} step={0.1} min={0} max={30} testId="emi-rate" />
          <SliderField label="Tenure (years)" value={years} onChange={setYears} min={1} max={30} testId="emi-years" />
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="emi-result">
          <div className="eyebrow mb-2">Monthly EMI</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={emi} testId="emi-monthly" />
          </div>

          {/* Split bar */}
          <div className="mt-8">
            <div className="flex items-center gap-2 eyebrow mb-2">
              <span>Principal vs. Interest</span>
            </div>
            <div className="w-full h-3 rounded-sm overflow-hidden border hairline flex">
              <div style={{ width: `${principalPct}%` }} className="bg-ink transition-all duration-500" />
              <div style={{ width: `${interestPct}%` }} className="bg-brass transition-all duration-500" />
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-ink-soft">
              <span><span className="inline-block w-2 h-2 bg-ink mr-1.5" /> Principal · {principalPct.toFixed(0)}%</span>
              <span><span className="inline-block w-2 h-2 bg-brass mr-1.5" /> Interest · {interestPct.toFixed(0)}%</span>
            </div>
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Principal" value={formatINR(principal)} />
            <Row label="Total Interest" value={formatINR(totalInterest)} accent />
            <Row label="Total Payment" value={formatINR(totalPayment)} />
          </div>

          {principal > 0 && years > 0 && (
            <p className="mt-6 text-[14px] text-ink-soft leading-relaxed">
              Over <span className="num text-ink">{years} years</span>, you repay about{" "}
              <span className="num text-brass-deep font-medium">{multiple.toFixed(2)}×</span> what you
              borrowed — <span className="num text-ink">{formatCompactINR(totalInterest)}</span> in interest alone.
            </p>
          )}
        </div>
      </div>

      {rows.length > 0 && (
        <YearBreakdown
          rows={rows}
          primaryLabel="Outstanding balance at end of each year"
          tableHeaders={["Year", "Interest Paid to Date", "Principal Paid to Date", "Outstanding Balance"]}
          testIdPrefix="emi"
        />
      )}

      <CalculatorActions
        testIdPrefix="emi"
        buildPayload={() => ({
          type: "emi",
          title: "Loan EMI Calculator",
          one_liner: `a loan EMI plan — ${formatINR(principal)} at ${rate}% for ${years} years, ${formatINR(emi)} per month`,
          inputs: [
            { label: "Loan Amount", value: formatINR(principal) },
            { label: "Interest Rate", value: `${rate}% p.a.` },
            { label: "Tenure", value: `${years} years` },
          ],
          hero: { label: "Monthly EMI", value: formatINR(emi) },
          summary: [
            { label: "Principal", value: formatINR(principal) },
            { label: "Total Interest", value: formatINR(totalInterest), accent: true },
            { label: "Total Payment", value: formatINR(totalPayment) },
          ],
          row_headers: ["Year", "Interest Paid to Date", "Principal Paid to Date", "Outstanding Balance"],
          rows: rows.map((r) => [
            String(r.year), r.columns[0].value, r.columns[1].value, r.columns[2].value,
          ]),
        })}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}

function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}

function SliderField({ label, value, onChange, min, max, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        <span className="num text-ink text-[14px]">{value} yrs</span>
      </div>
      <input
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#14A5A5]" data-testid={testId}
      />
      <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num">
        <span>{min}</span><span>{max}</span>
      </div>
    </label>
  );
}
